// app/frontend/src/constants/pageThemes.ts
//
// The dark templates an organizer (or artist) can dress their pages in
// (#2230 / #2231). Started as the Dance Card templates; now one list for the
// Dance Card, the organizer page and event pages. Ids must match
// PageTheme::TEMPLATE_IDS on the server (a spec checks). Every template stays
// dark: a Jest test fails if a background stop gets light.

export type TemplateId =
  | 'midnight-gradient'
  | 'ocean-blur'
  | 'sunset-glass'
  | 'forest-mist'
  | 'purple-haze'
  | 'minimal-dark'
  | 'wcwd-orange'
  | 'orange-sunset'
  | 'rose-gold'
  | 'gold'
  | 'silver'
  | 'bronze'
  // Moving backgrounds (#2250)
  | 'aurora'
  | 'nebula'
  | 'bokeh'
  | 'gradient-flow'
  | 'disco-sparkle'
  | 'night-flow'
  | 'red-velvet'
  | 'rose-glow'
  | 'city-lights'
  | 'fireflies'
  | 'silver-mist'
  | 'ocean-tide'
  | 'golden-hour'
  | 'neon-night'
  | 'stage-lights'
  | 'smoke-haze'
  | 'confetti'
  | 'seaside'
  | 'petals'
  | 'snowfall'
  | 'kage-embers';

/** The kinds of moving background a template can have (#2250). */
export type ThemeMotion =
  | 'aurora' | 'nebula' | 'bokeh' | 'flow' | 'sparkle'
  // #2285
  | 'spotlights' | 'haze' | 'waves' | 'confetti' | 'petals' | 'snow'
  // Neon Night's lens-flare glints and Fireflies' drifting glows
  | 'flare' | 'fireflies';

export interface Template {
  name: string;
  preview: string; // CSS background for preview thumbnail
  background: string; // Full CSS background
  cardBackground: string; // Background for cards/blocks
  textColor: string;
  accentColor: string;
  borderColor: string;
  /** The background color most of the page sits on; the accent's contrast is checked against it. */
  baseColor: string;
  /**
   * The accents organizers can pick on this template (#2249), besides its own
   * accentColor. Curated so each reads on the template without adjustment;
   * there's no free hex field. A test holds every one to 3:1 on baseColor.
   */
  accents: string[];
  /**
   * A moving background drawn over `background` by ThemeBackdrop (#2250).
   * `background` stays the still frame: reduced motion, the first paint, and
   * anywhere the motion layer isn't drawn.
   */
  motion?: ThemeMotion;
  /** The colors the motion is drawn in (lights, bands, glints). */
  motionColors?: string[];
  /** Text and icons on an accent fill. Set by themedTemplate(); white when absent. */
  onAccentColor?: string;
  /**
   * Made for one event or organizer (#2256): set from the console, and left
   * out of the picker except on a page already wearing it.
   */
  custom?: boolean;
}

export const TEMPLATES: Record<TemplateId, Template> = {
  'midnight-gradient': {
    name: 'Midnight Gradient',
    preview: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#EA580C',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    baseColor: '#16213e',
    accents: ['#38bdf8', '#a78bfa', '#f472b6', '#facc15', '#34d399'],
  },
  'ocean-blur': {
    name: 'Ocean Blur',
    preview: 'linear-gradient(135deg, #0c3547 0%, #1a4b5c 50%, #0a5c6e 100%)',
    background: 'linear-gradient(180deg, #0c3547 0%, #1a4b5c 40%, #0a5c6e 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.08)',
    textColor: '#ffffff',
    accentColor: '#00d4ff',
    borderColor: 'rgba(255, 255, 255, 0.15)',
    baseColor: '#1a4b5c',
    accents: ['#5eead4', '#fde68a', '#f9a8d4', '#a3e635', '#fdba74'],
  },
  'sunset-glass': {
    name: 'Sunset Glass',
    preview: 'linear-gradient(135deg, #2d1f3d 0%, #4a2c4f 50%, #6b3a5c 100%)',
    background: 'linear-gradient(180deg, #2d1f3d 0%, #4a2c4f 40%, #6b3a5c 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.06)',
    textColor: '#ffffff',
    accentColor: '#ff6b6b',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#4a2c4f',
    accents: ['#fbbf24', '#f9a8d4', '#fdba74', '#c4b5fd', '#5eead4'],
  },
  'forest-mist': {
    name: 'Forest Mist',
    preview: 'linear-gradient(135deg, #1a2e1a 0%, #2d4a2d 50%, #1e3d1e 100%)',
    background: 'linear-gradient(180deg, #1a2e1a 0%, #2d4a2d 40%, #1e3d1e 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.07)',
    textColor: '#ffffff',
    accentColor: '#4ade80',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    baseColor: '#2d4a2d',
    accents: ['#facc15', '#fdba74', '#f9a8d4', '#7dd3fc', '#e5e7eb'],
  },
  'purple-haze': {
    name: 'Purple Haze',
    preview: 'linear-gradient(135deg, #1e1e3f 0%, #2d2d5e 50%, #3d3d7f 100%)',
    background: 'linear-gradient(180deg, #1e1e3f 0%, #2d2d5e 40%, #3d3d7f 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.08)',
    textColor: '#ffffff',
    accentColor: '#a855f7',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#2d2d5e',
    accents: ['#f0abfc', '#facc15', '#67e8f9', '#fb7185', '#86efac'],
  },
  'minimal-dark': {
    name: 'Minimal Dark',
    preview: '#0a0a0a',
    background: '#0a0a0a',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#EA580C',
    borderColor: 'rgba(255, 255, 255, 0.08)',
    baseColor: '#0a0a0a',
    accents: ['#ef4444', '#ec4899', '#a855f7', '#3b82f6', '#22c55e', '#facc15'],
  },
  'wcwd-orange': {
    name: 'WCWD',
    preview: 'linear-gradient(135deg, #1a0a00 0%, #2d1810 50%, #1a0f0a 100%)',
    background: 'linear-gradient(180deg, #1a0a00 0%, #2d1810 40%, #1a0f0a 100%)',
    cardBackground: 'rgba(234, 88, 12, 0.1)',
    textColor: '#ffffff',
    accentColor: '#EA580C',
    borderColor: 'rgba(234, 88, 12, 0.3)',
    baseColor: '#2d1810',
    accents: ['#fdba74', '#facc15', '#f472b6', '#fca5a5', '#5eead4'],
  },
  'orange-sunset': {
    name: 'Orange Sunset',
    preview: 'linear-gradient(135deg, #1a1a2e 0%, #3d1f1f 50%, #5c2a1a 100%)',
    background: 'linear-gradient(180deg, #1a1a2e 0%, #3d1f1f 40%, #5c2a1a 70%, #EA580C 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.08)',
    textColor: '#ffffff',
    accentColor: '#ffb347',
    borderColor: 'rgba(255, 179, 71, 0.2)',
    baseColor: '#3d1f1f',
    accents: ['#fb7185', '#fde68a', '#f9a8d4', '#fdba74', '#5eead4'],
  },
  'rose-gold': {
    name: 'Rose Gold',
    preview: 'linear-gradient(135deg, #2d1f2d 0%, #3d2a3a 50%, #4a3545 100%)',
    background: 'linear-gradient(180deg, #2d1f2d 0%, #3d2a3a 40%, #4a3545 70%, #5c4050 100%)',
    cardBackground: 'rgba(255, 182, 193, 0.08)',
    textColor: '#ffffff',
    accentColor: '#e8b4b8',
    borderColor: 'rgba(232, 180, 184, 0.25)',
    baseColor: '#3d2a3a',
    accents: ['#f9a8d4', '#fcd34d', '#c4b5fd', '#fda4af', '#99f6e4'],
  },
  'gold': {
    name: 'Gold',
    preview: 'linear-gradient(135deg, #1a1508 0%, #2d2510 50%, #3d3318 100%)',
    background: 'linear-gradient(180deg, #1a1508 0%, #2d2510 40%, #3d3318 100%)',
    cardBackground: 'rgba(255, 215, 0, 0.08)',
    textColor: '#ffffff',
    accentColor: '#ffd700',
    borderColor: 'rgba(255, 215, 0, 0.3)',
    baseColor: '#2d2510',
    accents: ['#fbbf24', '#fdba74', '#fef3c7', '#fca5a5', '#7dd3fc'],
  },
  'silver': {
    name: 'Silver',
    preview: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 50%, #3a3a3a 100%)',
    background: 'linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 40%, #3a3a3a 100%)',
    cardBackground: 'rgba(192, 192, 192, 0.08)',
    textColor: '#ffffff',
    accentColor: '#c0c0c0',
    borderColor: 'rgba(192, 192, 192, 0.3)',
    baseColor: '#2a2a2a',
    accents: ['#e5e7eb', '#93c5fd', '#f9a8d4', '#fde68a', '#86efac'],
  },
  'bronze': {
    name: 'Bronze',
    preview: 'linear-gradient(135deg, #1a1210 0%, #2d1f18 50%, #3d2a20 100%)',
    background: 'linear-gradient(180deg, #1a1210 0%, #2d1f18 40%, #3d2a20 100%)',
    cardBackground: 'rgba(205, 127, 50, 0.08)',
    textColor: '#ffffff',
    accentColor: '#cd7f32',
    borderColor: 'rgba(205, 127, 50, 0.3)',
    baseColor: '#2d1f18',
    accents: ['#fdba74', '#fbbf24', '#fca5a5', '#e7c9a9', '#5eead4'],
  },
  // ── Moving backgrounds (#2250). baseColor is the lit middle of the motion,
  //    not the darkest stop, so accents are checked against the bright part.
  aurora: {
    name: 'Aurora',
    preview: 'linear-gradient(135deg, #07121f 0%, #0f2a3a 50%, #1b1640 100%)',
    background: 'linear-gradient(180deg, #07121f 0%, #0b1a2a 50%, #0a1322 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.06)',
    textColor: '#ffffff',
    accentColor: '#5eead4',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#163449',
    accents: ['#a78bfa', '#86efac', '#f9a8d4', '#fde68a', '#7dd3fc'],
    motion: 'aurora',
    motionColors: ['#22d3ee', '#a855f7', '#34d399'],
  },
  nebula: {
    name: 'Nebula',
    preview: 'radial-gradient(circle at 30% 30%, #3b1f6e 0%, #0d0b24 60%, #05040f 100%)',
    background: 'linear-gradient(180deg, #05040f 0%, #0d0b24 50%, #120a26 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.06)',
    textColor: '#ffffff',
    accentColor: '#c4b5fd',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#22184a',
    accents: ['#f0abfc', '#7dd3fc', '#fde68a', '#fb7185', '#86efac'],
    motion: 'nebula',
    motionColors: ['#7c3aed', '#db2777', '#ffffff'],
  },
  bokeh: {
    name: 'Bokeh Lights',
    preview: 'radial-gradient(circle at 70% 40%, #5a3a12 0%, #1c130c 55%, #120d08 100%)',
    background: 'linear-gradient(180deg, #120d08 0%, #1c130c 50%, #140e09 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.06)',
    textColor: '#ffffff',
    accentColor: '#fbbf24',
    borderColor: 'rgba(251, 191, 36, 0.2)',
    baseColor: '#33230f',
    accents: ['#fdba74', '#f9a8d4', '#fde68a', '#5eead4', '#fca5a5'],
    motion: 'bokeh',
    motionColors: ['#f59e0b', '#fb7185', '#fde68a'],
  },
  'gradient-flow': {
    name: 'Gradient Flow',
    preview: 'linear-gradient(120deg, #1e1b4b 0%, #4c1d95 35%, #831843 70%, #1e3a8a 100%)',
    background: 'linear-gradient(120deg, #1e1b4b 0%, #4c1d95 35%, #831843 70%, #1e3a8a 100%)',
    cardBackground: 'rgba(0, 0, 0, 0.25)',
    textColor: '#ffffff',
    accentColor: '#f9a8d4',
    borderColor: 'rgba(255, 255, 255, 0.14)',
    baseColor: '#4c1d95',
    accents: ['#fcd34d', '#fde68a', '#7dd3fc', '#86efac', '#fdba74', '#e5e7eb'],
    motion: 'flow',
  },
  'disco-sparkle': {
    name: 'Disco Sparkle',
    preview: 'conic-gradient(from 200deg at 50% 0%, #15121f, #2a1d3d, #15121f, #241a33, #15121f)',
    background: 'linear-gradient(180deg, #0a0a12 0%, #15121f 50%, #0c0a14 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.06)',
    textColor: '#ffffff',
    accentColor: '#e879f9',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#241c36',
    accents: ['#fde68a', '#67e8f9', '#f9a8d4', '#86efac', '#fdba74'],
    motion: 'sparkle',
    motionColors: ['#ffffff', '#fde68a', '#e879f9'],
  },
  // Gradient Flow's colors as dark mode: near-black, with indigo, violet and
  // magenta as slow glows instead of a full-bleed gradient.
  'night-flow': {
    name: 'Night Flow',
    preview: 'radial-gradient(circle at 25% 30%, #2e1a6b 0%, transparent 55%), radial-gradient(circle at 80% 70%, #5a1240 0%, transparent 55%), #09081a',
    background: 'linear-gradient(160deg, #08071a 0%, #0d0b22 55%, #120a1f 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#fcd34d',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    baseColor: '#1a1440',
    accents: ['#f9a8d4', '#c4b5fd', '#7dd3fc', '#86efac', '#fdba74'],
    motion: 'aurora',
    motionColors: ['#4338ca', '#7c3aed', '#be185d'],
  },
  // ── One per color family (#2283), each reusing a motion above. Near-black
  //    grounds with the colors as glows, like Night Flow.
  'red-velvet': {
    name: 'Red Velvet',
    preview: 'radial-gradient(circle at 30% 35%, #5c0f14 0%, transparent 55%), radial-gradient(circle at 75% 70%, #4a0a1e 0%, transparent 55%), #0a0405',
    background: 'linear-gradient(170deg, #0a0405 0%, #140607 55%, #1a0708 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#fca5a5',
    borderColor: 'rgba(252, 165, 165, 0.16)',
    baseColor: '#2a0c10',
    accents: ['#fde68a', '#fdba74', '#f9a8d4', '#fcd34d', '#e5e7eb'],
    motion: 'aurora',
    motionColors: ['#b91c1c', '#7f1d1d', '#e11d48'],
  },
  'rose-glow': {
    name: 'Rose Glow',
    preview: 'radial-gradient(circle at 30% 35%, #5c1a3a 0%, transparent 55%), radial-gradient(circle at 75% 70%, #4a1030 0%, transparent 55%), #0c0609',
    background: 'linear-gradient(170deg, #0c0609 0%, #160a10 55%, #1a0b14 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#f9a8d4',
    borderColor: 'rgba(249, 168, 212, 0.16)',
    baseColor: '#2a1020',
    accents: ['#fda4af', '#fde68a', '#c4b5fd', '#99f6e4', '#e5e7eb'],
    motion: 'aurora',
    motionColors: ['#db2777', '#f472b6', '#be185d'],
  },
  'city-lights': {
    name: 'City Lights',
    preview: 'radial-gradient(circle at 70% 40%, #12366e 0%, transparent 55%), radial-gradient(circle at 25% 75%, #0d2a5a 0%, transparent 50%), #040814',
    background: 'linear-gradient(180deg, #040814 0%, #071330 55%, #0a1a3a 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#38bdf8',
    borderColor: 'rgba(56, 189, 248, 0.18)',
    baseColor: '#10254a',
    accents: ['#7dd3fc', '#93c5fd', '#5eead4', '#fde68a', '#e5e7eb'],
    motion: 'bokeh',
    motionColors: ['#1e6fd9', '#4fb8f5', '#93c5fd'],
  },
  fireflies: {
    name: 'Fireflies',
    preview: 'radial-gradient(circle at 30% 60%, #1c3a20 0%, transparent 55%), radial-gradient(circle at 70% 30%, #2a3a10 0%, transparent 45%), #040a06',
    background: 'linear-gradient(180deg, #040a06 0%, #07120b 55%, #09160d 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#86efac',
    borderColor: 'rgba(134, 239, 172, 0.16)',
    baseColor: '#13261a',
    accents: ['#a3e635', '#fde68a', '#fcd34d', '#5eead4', '#e5e7eb'],
    motion: 'fireflies',
    motionColors: ['#bef264', '#4ade80', '#fde68a'],
  },
  'silver-mist': {
    name: 'Silver Mist',
    preview: 'radial-gradient(circle at 30% 30%, #3a3a40 0%, transparent 55%), radial-gradient(circle at 75% 70%, #2a2a30 0%, transparent 50%), #09090b',
    background: 'linear-gradient(180deg, #09090b 0%, #111114 55%, #131316 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#e5e7eb',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#1f1f24',
    accents: ['#93c5fd', '#f9a8d4', '#fde68a', '#86efac', '#fdba74'],
    motion: 'nebula',
    motionColors: ['#a1a1aa', '#e5e7eb'],
  },
  // The flow motion pans the background itself, so it's a dark sea gradient.
  'ocean-tide': {
    name: 'Ocean Tide',
    preview: 'linear-gradient(120deg, #020b14 0%, #04263a 40%, #053a46 65%, #021018 100%)',
    background: 'linear-gradient(120deg, #020b14 0%, #04263a 35%, #053a46 60%, #021018 100%)',
    cardBackground: 'rgba(0, 0, 0, 0.25)',
    textColor: '#ffffff',
    accentColor: '#67e8f9',
    borderColor: 'rgba(103, 232, 249, 0.16)',
    baseColor: '#06303c',
    accents: ['#5eead4', '#7dd3fc', '#99f6e4', '#fde68a', '#e5e7eb'],
    motion: 'flow',
  },
  'golden-hour': {
    name: 'Golden Hour',
    preview: 'radial-gradient(circle at 30% 70%, #5a2e0a 0%, transparent 55%), radial-gradient(circle at 75% 35%, #4a1a2a 0%, transparent 50%), #0f0805',
    background: 'linear-gradient(170deg, #0f0805 0%, #1a0d08 55%, #1c0b10 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#fcd34d',
    borderColor: 'rgba(252, 211, 77, 0.16)',
    baseColor: '#2e1810',
    accents: ['#fdba74', '#f9a8d4', '#fde68a', '#fca5a5', '#e5e7eb'],
    motion: 'aurora',
    motionColors: ['#f59e0b', '#fb923c', '#f472b6'],
  },
  'neon-night': {
    name: 'Neon Night',
    preview: 'conic-gradient(from 200deg at 50% 0%, #0b0716, #2a0d36, #0b0716, #082a36, #0b0716)',
    background: 'linear-gradient(180deg, #06040d 0%, #0b0716 50%, #0a0612 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#f472b6',
    borderColor: 'rgba(244, 114, 182, 0.18)',
    baseColor: '#1b1230',
    accents: ['#67e8f9', '#f0abfc', '#c4b5fd', '#fde68a', '#86efac'],
    motion: 'flare',
    motionColors: ['#22d3ee', '#f472b6', '#e879f9'],
  },
  // ── New motions (#2285): one theme for each.
  'stage-lights': {
    name: 'Stage Lights',
    preview: 'radial-gradient(ellipse at 25% 0%, #4a3a12 0%, transparent 50%), radial-gradient(ellipse at 75% 0%, #3a1230 0%, transparent 50%), #07060a',
    background: 'linear-gradient(180deg, #07060a 0%, #0c0a10 60%, #100c14 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#fde68a',
    borderColor: 'rgba(253, 230, 138, 0.16)',
    baseColor: '#1e1a24',
    accents: ['#f9a8d4', '#93c5fd', '#fcd34d', '#c4b5fd', '#e5e7eb'],
    motion: 'spotlights',
    motionColors: ['#fde68a', '#f472b6', '#60a5fa'],
  },
  'smoke-haze': {
    name: 'Smoke & Haze',
    preview: 'radial-gradient(ellipse at 40% 70%, #3a3a42 0%, transparent 60%), radial-gradient(ellipse at 70% 30%, #3a1414 0%, transparent 50%), #08090d',
    background: 'linear-gradient(180deg, #08090d 0%, #0e0f13 55%, #120d0e 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#fca5a5',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#1f1f26',
    accents: ['#e5e7eb', '#fde68a', '#fdba74', '#c4b5fd', '#f9a8d4'],
    motion: 'haze',
    motionColors: ['#6b7280', '#9ca3af', '#7f1d1d'],
  },
  confetti: {
    name: 'Confetti',
    preview: 'radial-gradient(circle at 20% 30%, #4a1a3a 0%, transparent 35%), radial-gradient(circle at 75% 60%, #123a44 0%, transparent 35%), radial-gradient(circle at 50% 85%, #4a3a12 0%, transparent 30%), #0a0812',
    background: 'linear-gradient(180deg, #0a0812 0%, #100c1a 55%, #0d0a16 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#fcd34d',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#201a30',
    accents: ['#f9a8d4', '#67e8f9', '#86efac', '#c4b5fd', '#fdba74'],
    motion: 'confetti',
    motionColors: ['#f472b6', '#fde68a', '#67e8f9', '#86efac', '#c4b5fd'],
  },
  seaside: {
    name: 'Seaside',
    preview: 'radial-gradient(ellipse at 50% 110%, #0e5a6e 0%, transparent 60%), radial-gradient(ellipse at 30% 120%, #12306e 0%, transparent 55%), #03101a',
    background: 'linear-gradient(180deg, #03101a 0%, #051826 60%, #061e2c 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#5eead4',
    borderColor: 'rgba(94, 234, 212, 0.16)',
    baseColor: '#0a2a3a',
    accents: ['#7dd3fc', '#99f6e4', '#fde68a', '#fdba74', '#e5e7eb'],
    motion: 'waves',
    motionColors: ['#0e7490', '#22d3ee', '#1d4ed8'],
  },
  petals: {
    name: 'Petals',
    preview: 'radial-gradient(circle at 30% 30%, #4a1a30 0%, transparent 45%), radial-gradient(circle at 70% 70%, #3a1424 0%, transparent 45%), #0d0709',
    background: 'linear-gradient(170deg, #0d0709 0%, #150a0f 55%, #180b12 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#f9a8d4',
    borderColor: 'rgba(249, 168, 212, 0.16)',
    baseColor: '#2a1220',
    accents: ['#fda4af', '#fde68a', '#c4b5fd', '#86efac', '#e5e7eb'],
    motion: 'petals',
    motionColors: ['#f9a8d4', '#fda4af', '#fbcfe8'],
  },
  snowfall: {
    name: 'Snowfall',
    preview: 'radial-gradient(circle at 50% 20%, #1a2a44 0%, transparent 60%), #060a12',
    background: 'linear-gradient(180deg, #060a12 0%, #0a1220 55%, #0c1626 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#7dd3fc',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    baseColor: '#14223a',
    accents: ['#e5e7eb', '#93c5fd', '#c4b5fd', '#fca5a5', '#86efac'],
    motion: 'snow',
    motionColors: ['#ffffff', '#e0f2fe'],
  },
  // Custom for KSX 2026 Kage Edition (#2256), from its flyer: black ink, with
  // gold and blood-red embers rising off it like sparks.
  'kage-embers': {
    name: 'Kage Embers',
    preview: 'radial-gradient(circle at 30% 70%, #5a3a0c 0%, transparent 45%), radial-gradient(circle at 75% 30%, #5a1010 0%, transparent 45%), #070605',
    background: 'linear-gradient(180deg, #050505 0%, #0b0907 55%, #120806 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.05)',
    textColor: '#ffffff',
    accentColor: '#fbbf24',
    borderColor: 'rgba(251, 191, 36, 0.18)',
    baseColor: '#2a160c',
    accents: ['#fde68a', '#ef4444', '#fca5a5', '#fdba74', '#e5e7eb'],
    motion: 'bokeh',
    motionColors: ['#d4a017', '#b91c1c', '#f59e0b'],
    custom: true,
  },
};

export const getTemplate = (templateId: string): Template => {
  return TEMPLATES[templateId as TemplateId] || TEMPLATES['midnight-gradient'];
};

/** Every template id, in picker order. */
export const TEMPLATE_IDS = Object.keys(TEMPLATES) as TemplateId[];

/** Names for the curated accents, for the swatches' labels (screen readers, tooltips). */
export const ACCENT_NAMES: Record<string, string> = {
  '#38bdf8': 'Sky', '#a78bfa': 'Violet', '#f472b6': 'Pink', '#facc15': 'Yellow', '#34d399': 'Mint',
  '#5eead4': 'Teal', '#fde68a': 'Butter', '#f9a8d4': 'Blush', '#a3e635': 'Lime', '#fdba74': 'Peach',
  '#fbbf24': 'Amber', '#c4b5fd': 'Lavender', '#7dd3fc': 'Ice blue', '#e5e7eb': 'Pearl', '#f0abfc': 'Orchid',
  '#67e8f9': 'Cyan', '#fb7185': 'Coral', '#86efac': 'Spring green', '#ef4444': 'Red', '#ec4899': 'Hot pink',
  '#a855f7': 'Purple', '#3b82f6': 'Blue', '#22c55e': 'Green', '#fca5a5': 'Rose', '#fcd34d': 'Honey',
  '#fda4af': 'Petal', '#99f6e4': 'Seafoam', '#fef3c7': 'Cream', '#93c5fd': 'Periwinkle', '#e7c9a9': 'Sand',
  '#06b6d4': 'Ocean',
};
